import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Loader2, Plus, Trash2, Power, MapPin, Repeat, Edit2, Send } from "lucide-react";
import api from "../../lib/api";

/**
 * PickupSlotsPanel — Admin → Shop → Pickup slots.
 *
 * Admin publishes either a one-time window or a recurring weekly window.
 * Slots become assignable to COD orders (via the Order detail modal),
 * which populates the customer's confirmation email + on-page receipt.
 */
const WEEKDAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const EMPTY = {
    kind: "one_time",
    label: "",
    location: "",
    notes: "",
    active: true,
    start_at: "",
    end_at: "",
    weekday: 4,          // default to Friday
    start_time: "15:00",
    end_time: "17:00",
};

const PickupSlotsPanel = () => {
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);       // slot id being edited, or "new"
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/pickup-slots");
            setSlots(Array.isArray(r.data) ? r.data : []);
        } catch (e) {
            toast.error("Failed to load pickup slots");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(EMPTY); setEditing("new"); };
    const openEdit = (s) => {
        setForm({
            kind: s.kind || "one_time",
            label: s.label || "",
            location: s.location || "",
            notes: s.notes || "",
            active: !!s.active,
            start_at: s.start_at || "",
            end_at: s.end_at || "",
            weekday: s.weekday ?? 4,
            start_time: s.start_time || "15:00",
            end_time: s.end_time || "17:00",
        });
        setEditing(s.id);
    };
    const cancel = () => { setEditing(null); setForm(EMPTY); };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...form };
            // Trim fields not relevant to the selected kind so the backend
            // validator doesn't complain about incomplete pairs.
            if (payload.kind === "one_time") {
                delete payload.weekday; delete payload.start_time; delete payload.end_time;
            } else {
                delete payload.start_at; delete payload.end_at;
            }
            if (editing === "new") {
                await api.post("/admin/pickup-slots", payload);
                toast.success("Pickup slot published");
            } else {
                await api.put(`/admin/pickup-slots/${editing}`, payload);
                toast.success("Pickup slot updated");
            }
            cancel();
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const toggle = async (s) => {
        try {
            await api.put(`/admin/pickup-slots/${s.id}`, { active: !s.active });
            load();
        } catch (e) {
            toast.error("Failed to toggle slot");
        }
    };
    const remove = async (s) => {
        if (!window.confirm(`Delete "${s.display}"? Orders already assigned keep their snapshot.`)) return;
        try {
            await api.delete(`/admin/pickup-slots/${s.id}`);
            load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const [bulkBusy, setBulkBusy] = useState(null);
    const bulkAssign = async (s) => {
        if (!window.confirm(
            `Assign "${s.display}" to every open COD/pickup order that doesn't already have a slot?\n\n` +
            `Customers will be re-emailed with the updated pickup window.`
        )) return;
        setBulkBusy(s.id);
        try {
            const { data } = await api.post(`/admin/pickup-slots/${s.id}/bulk-assign`);
            if (data.assigned === 0) {
                toast("No open orders needed a slot.");
            } else {
                toast.success(`Assigned to ${data.assigned} order${data.assigned === 1 ? "" : "s"} · customers re-emailed`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk assign failed");
        } finally {
            setBulkBusy(null);
        }
    };

    return (
        <div className="space-y-6" data-testid="pickup-slots-panel">
            <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium tracking-tight">Pickup slots</h2>
                        <p className="text-sm text-[#4A5568] mt-1">
                            Designate one-time or recurring windows when COD customers can pick up their orders.
                            Assign a slot to any order from the order detail modal — the customer&apos;s email + receipt update automatically.
                        </p>
                    </div>
                    <button
                        onClick={openNew}
                        data-testid="pickup-slot-add"
                        className="inline-flex items-center gap-2 bg-[#E53935] text-white hover:bg-[#c62828] rounded-full px-4 py-2 text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" /> New slot
                    </button>
                </div>
            </div>

            {editing && (
                <div className="bg-white border border-black/10 rounded-2xl p-5 sm:p-6" data-testid="pickup-slot-form">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-heading text-lg text-[#1C252E] font-medium">
                            {editing === "new" ? "Publish a pickup slot" : "Edit slot"}
                        </h3>
                        <button onClick={cancel} className="text-sm text-[#4A5568] hover:text-[#1C252E]">Cancel</button>
                    </div>

                    <div className="flex gap-2 mb-4">
                        {["one_time", "recurring"].map((k) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setForm({ ...form, kind: k })}
                                data-testid={`pickup-kind-${k}`}
                                className={`px-4 py-2 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                                    form.kind === k
                                        ? "bg-[#1C252E] text-white border-[#1C252E]"
                                        : "bg-white text-[#4A5568] border-black/15 hover:bg-[#F4F1EA]"
                                }`}
                            >
                                {k === "one_time" ? <Calendar className="w-3.5 h-3.5" /> : <Repeat className="w-3.5 h-3.5" />}
                                {k === "one_time" ? "One-time" : "Recurring weekly"}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="Label" hint="Shown to customer">
                            <input type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                                data-testid="pickup-label" placeholder="Front office pickup" className={inputCls} />
                        </Field>
                        <Field label="Location">
                            <input type="text" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                                data-testid="pickup-location" placeholder="Frenchtown School — main office" className={inputCls} />
                        </Field>

                        {form.kind === "one_time" ? (
                            <>
                                <Field label="Starts">
                                    <input type="datetime-local" value={form.start_at.slice(0, 16)}
                                        onChange={(e) => setForm({ ...form, start_at: new Date(e.target.value).toISOString() })}
                                        data-testid="pickup-start-at" className={inputCls} />
                                </Field>
                                <Field label="Ends">
                                    <input type="datetime-local" value={form.end_at.slice(0, 16)}
                                        onChange={(e) => setForm({ ...form, end_at: new Date(e.target.value).toISOString() })}
                                        data-testid="pickup-end-at" className={inputCls} />
                                </Field>
                            </>
                        ) : (
                            <>
                                <Field label="Weekday">
                                    <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
                                        data-testid="pickup-weekday" className={inputCls}>
                                        {WEEKDAY_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
                                    </select>
                                </Field>
                                <div className="grid grid-cols-2 gap-2">
                                    <Field label="From">
                                        <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                                            data-testid="pickup-start-time" className={inputCls} />
                                    </Field>
                                    <Field label="To">
                                        <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                                            data-testid="pickup-end-time" className={inputCls} />
                                    </Field>
                                </div>
                            </>
                        )}

                        <Field label="Notes" hint="Shown under the slot on the receipt">
                            <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                data-testid="pickup-notes" placeholder="Enter through the east door" className={inputCls} />
                        </Field>
                        <div className="flex items-end">
                            <label className="flex items-center gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                    data-testid="pickup-active" className="w-4 h-4 accent-[#E53935]" />
                                Active
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-5">
                        <button onClick={cancel} className="px-5 py-2 rounded-full border border-black/15 text-sm hover:bg-[#F4F1EA]">Cancel</button>
                        <button onClick={save} disabled={saving} data-testid="pickup-save"
                            className="px-5 py-2 rounded-full bg-[#E53935] text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2">
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing === "new" ? "Publish slot" : "Save"}
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-black/10 rounded-2xl">
                <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
                    <h3 className="font-heading text-lg text-[#1C252E] font-medium">Published slots</h3>
                    <span className="text-xs text-[#4A5568]">{slots.length} total · {slots.filter((s) => s.active).length} active</span>
                </div>
                {loading ? (
                    <div className="py-12 text-center text-[#4A5568] flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                    </div>
                ) : slots.length === 0 ? (
                    <div className="py-12 text-center text-sm text-[#4A5568]">
                        No pickup slots yet. Publish your first one to start assigning them to COD orders.
                    </div>
                ) : (
                    <ul className="divide-y divide-black/5">
                        {slots.map((s) => (
                            <li key={s.id} data-testid={`pickup-slot-row-${s.id}`}
                                className={`px-5 py-4 flex items-start gap-3 ${s.active ? "" : "opacity-60"}`}>
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                    s.kind === "recurring" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"
                                }`}>
                                    {s.kind === "recurring" ? <Repeat className="w-4 h-4" /> : <Calendar className="w-4 h-4" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#1C252E]">{s.display}</p>
                                    {s.label && <p className="text-xs text-[#4A5568]">{s.label}</p>}
                                    {s.location && (
                                        <p className="text-xs text-[#4A5568] flex items-center gap-1 mt-0.5">
                                            <MapPin className="w-3 h-3" /> {s.location}
                                        </p>
                                    )}
                                    {s.notes && <p className="text-xs text-[#4A5568] mt-0.5 italic">{s.notes}</p>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => bulkAssign(s)}
                                        disabled={!s.active || bulkBusy === s.id}
                                        title={s.active ? "Assign this slot to all open COD orders that don't have one yet" : "Activate the slot to enable bulk assign"}
                                        data-testid={`pickup-bulk-assign-${s.id}`}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E53935] text-[#E53935] hover:bg-[#FEF2F2] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 text-xs font-medium"
                                    >
                                        {bulkBusy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                        Assign to open COD
                                    </button>
                                    <button onClick={() => toggle(s)} title={s.active ? "Turn off" : "Turn on"}
                                        data-testid={`pickup-toggle-${s.id}`}
                                        className={`p-2 rounded-lg hover:bg-[#F4F1EA] ${s.active ? "text-emerald-700" : "text-[#4A5568]"}`}>
                                        <Power className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => openEdit(s)} title="Edit" data-testid={`pickup-edit-${s.id}`}
                                        className="p-2 rounded-lg hover:bg-[#F4F1EA] text-[#4A5568]">
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => remove(s)} title="Delete" data-testid={`pickup-delete-${s.id}`}
                                        className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 text-[#4A5568]">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

const inputCls = "w-full px-3 py-2 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-[#E53935]";

const Field = ({ label, hint, children }) => (
    <div>
        <label className="block text-xs font-medium uppercase tracking-wider text-[#4A5568] mb-1.5">
            {label}
            {hint ? <span className="text-[#4A5568]/60 font-normal normal-case tracking-normal"> · {hint}</span> : null}
        </label>
        {children}
    </div>
);

export default PickupSlotsPanel;
